import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";
import "./Calendar.css";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const PX_PER_HOUR = 48;

function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCalendars, setActiveCalendars] = useState(["private", "work", "holidays", "google", "apple", "other"]);
  const [activeTag, setActiveTag] = useState("");
  const [expandedDay, setExpandedDay] = useState(null);
  const navigate = useNavigate();
  const dayBodyRef = useRef(null);
  const weekBodyRef = useRef(null);

  // Close expanded day on outside click
  useEffect(() => {
    if (!expandedDay) return;
    const handler = (e) => {
      const expanded = document.querySelector(".month-cell.expanded");
      if (expanded && !expanded.contains(e.target)) {
        setExpandedDay(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expandedDay]);

  // Calculate date range for current view
  const dateRange = useMemo(() => {
    const d = new Date(currentDate);
    if (view === "day") {
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const end = new Date(start);
      end.setDate(end.getDate() + 1);
      return { start, end };
    } else if (view === "week") {
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Monday start
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return { start, end };
    } else {
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      // Extend to fill the grid
      const startDay = start.getDay() === 0 ? 6 : start.getDay() - 1;
      start.setDate(start.getDate() - startDay);
      end.setDate(end.getDate() + (7 - (end.getDay() === 0 ? 7 : end.getDay())));
      return { start, end };
    }
  }, [currentDate, view]);

  const toLocalISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const h = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${day}T${h}:${min}:${s}`;
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const start = toLocalISO(dateRange.start);
      const end = toLocalISO(dateRange.end);
      const data = await apiClient.get(`/schedules?start=${start}&end=${end}`);
      setEvents(data.schedules || []);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events by active calendars and tags
  const filteredEvents = useMemo(() => {
    let filtered = events.filter((e) => activeCalendars.includes(e.calendar || "private"));
    if (activeTag) {
      filtered = filtered.filter((e) => (e.tags || []).includes(activeTag));
    }
    return filtered;
  }, [events, activeCalendars, activeTag]);

  // Extract unique tags from all events
  const allTags = useMemo(() => {
    const tagSet = new Set();
    events.forEach((e) => (e.tags || []).forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [events]);

  const toggleCalendar = (cal) => {
    setActiveCalendars((prev) =>
      prev.includes(cal) ? prev.filter((c) => c !== cal) : [...prev, cal]
    );
  };

  // Auto-scroll day/week view to first event or 8am
  useEffect(() => {
    if (view !== "day" && view !== "week") return;
    const ref = view === "day" ? dayBodyRef : weekBodyRef;
    if (!ref.current) return;

    // Find earliest event hour for current view
    let scrollHour = 8; // default: 8am
    const viewEvents = view === "day"
      ? filteredEvents.filter((e) => {
          const ds = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
          return (e.startTime || "").slice(0, 10) === ds;
        })
      : filteredEvents;
    if (viewEvents.length > 0) {
      const hours = viewEvents.map((e) => parseInt((e.startTime || "").slice(11, 13)) || 0);
      const earliest = Math.min(...hours);
      scrollHour = Math.max(earliest - 1, 0); // 1 hour padding above
    }

    ref.current.scrollTop = scrollHour * PX_PER_HOUR;
  }, [view, filteredEvents, currentDate]);

  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (view === "day") d.setDate(d.getDate() + direction);
    else if (view === "week") d.setDate(d.getDate() + direction * 7);
    else d.setMonth(d.getMonth() + direction);
    setCurrentDate(d);
  };

  const goToToday = () => setCurrentDate(new Date());

  const formatHeader = () => {
    const opts = { year: "numeric", month: "long" };
    if (view === "day") return currentDate.toLocaleDateString(undefined, { ...opts, day: "numeric", weekday: "long" });
    if (view === "week") {
      const start = dateRange.start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      const end = new Date(dateRange.end);
      end.setDate(end.getDate() - 1);
      return `${start} – ${end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return currentDate.toLocaleDateString(undefined, opts);
  };

  const getEventsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return filteredEvents.filter((e) => {
      const start = (e.startTime || "").slice(0, 10);
      const end = (e.endTime || "").slice(0, 10);
      return dateStr >= start && dateStr <= end;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => date.getMonth() === currentDate.getMonth();

  const priorityColor = (p) => {
    const map = { critical: "#ef4444", high: "#f59e0b", normal: "#6366f1", low: "#94a3b8" };
    return map[p] || map.normal;
  };

  const calendarColor = (c) => {
    const map = { private: "#6366f1", work: "#3b82f6", holidays: "#10b981", google: "#ea4335", apple: "#a3aaae", other: "#8b5cf6" };
    return map[c] || map.private;
  };

  // Month grid: 6 rows x 7 cols
  const monthDays = useMemo(() => {
    const days = [];
    const d = new Date(dateRange.start);
    while (d <= dateRange.end) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [dateRange]);

  // Week days
  const weekDays = useMemo(() => {
    const days = [];
    const d = new Date(dateRange.start);
    for (let i = 0; i < 7; i++) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [dateRange]);

  const toDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Compute multi-day event layout for month view
  const monthEventRows = useMemo(() => {
    if (view !== "month") return {};
    const rows = {};
    const weekRows = [];
    for (let i = 0; i < monthDays.length; i += 7) {
      weekRows.push(monthDays.slice(i, i + 7));
    }

    for (const weekDaysArr of weekRows) {
      const weekStart = toDateStr(weekDaysArr[0]);
      const weekEnd = toDateStr(weekDaysArr[weekDaysArr.length - 1]);

      const weekEvents = filteredEvents.filter((e) => {
        const eStart = (e.startTime || "").slice(0, 10);
        const eEnd = (e.endTime || "").slice(0, 10);
        return eEnd >= weekStart && eStart <= weekEnd;
      });

      const lanes = [];

      for (const ev of weekEvents) {
        const eStart = (ev.startTime || "").slice(0, 10);
        const eEnd = (ev.endTime || "").slice(0, 10);

        let startCol = eStart < weekStart ? 0 : weekDaysArr.findIndex((d) => toDateStr(d) === eStart);
        let endCol = eEnd > weekEnd ? 6 : weekDaysArr.findIndex((d) => toDateStr(d) === eEnd);
        if (startCol < 0) startCol = 0;
        if (endCol < 0) endCol = 6;

        const span = endCol - startCol + 1;
        const isMultiDay = eStart !== eEnd;
        const isStart = eStart >= weekStart;
        const isEnd = eEnd <= weekEnd;

        let placed = false;
        for (let l = 0; l < lanes.length; l++) {
          const blocked = lanes[l].some(
            (item) => !(item.endCol < startCol || item.startCol > endCol)
          );
          if (!blocked) {
            lanes[l].push({ ev, startCol, endCol, span, isMultiDay, isStart, isEnd });
            placed = true;
            break;
          }
        }
        if (!placed) {
          lanes.push([{ ev, startCol, endCol, span, isMultiDay, isStart, isEnd }]);
        }
      }

      for (let c = 0; c < weekDaysArr.length; c++) {
        const ds = toDateStr(weekDaysArr[c]);
        const cellItems = [];
        for (let l = 0; l < lanes.length; l++) {
          const item = lanes[l].find((x) => x.startCol === c);
          const fillerItem = lanes[l].find((x) => x.startCol < c && x.endCol >= c);
          if (item) {
            cellItems.push({ ...item, lane: l });
          } else if (fillerItem) {
            cellItems.push({
              filler: true,
              lane: l,
              ev: fillerItem.ev,
              isMultiDay: true,
              isEnd: fillerItem.isEnd && c === fillerItem.endCol,
            });
          } else {
            cellItems.push({ spacer: true, lane: l });
          }
        }
        rows[ds] = { items: cellItems, totalLanes: lanes.length };
      }
    }
    return rows;
  }, [view, monthDays, filteredEvents]);

  const formatEventTooltip = (ev) => {
    const start = (ev.startTime || "").slice(11, 16);
    const end = (ev.endTime || "").slice(11, 16);
    const startDate = (ev.startTime || "").slice(0, 10);
    const endDate = (ev.endTime || "").slice(0, 10);
    let time = startDate === endDate ? `${start} – ${end}` : `${startDate} ${start} → ${endDate} ${end}`;
    if (start === "00:00" && (end === "23:59" || end === "00:00")) time = startDate === endDate ? "All day" : `${startDate} → ${endDate}`;
    let tip = `${ev.title}\n${time}`;
    if (ev.description) tip += `\n${ev.description}`;
    if ((ev.tags || []).length) tip += `\nTags: ${ev.tags.join(", ")}`;
    return tip;
  };

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button className="btn-secondary btn-sm" onClick={goToToday}>Today</button>
          <button className="btn-secondary btn-icon" onClick={() => navigateDate(-1)}>‹</button>
          <button className="btn-secondary btn-icon" onClick={() => navigateDate(1)}>›</button>
          <h2>{formatHeader()}</h2>
          {loading && <span className="loading-dot">●</span>}
        </div>
        <div className="calendar-controls">
          <div className="view-switcher">
            {["day", "week", "month"].map((v) => (
              <button
                key={v}
                className={view === v ? "btn-primary" : "btn-secondary"}
                onClick={() => setView(v)}
              >
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
          <RefreshButton onClick={fetchEvents} title="Refresh calendar" />
          <button className="btn-primary" onClick={() => navigate("/schedule/new")}>
            + New Event
          </button>
        </div>
      </div>

      <div className="calendar-filters">
        <div className="calendar-type-filters">
          {[
            { key: "private", label: "Private", color: "#6366f1" },
            { key: "work", label: "Work", color: "#3b82f6" },
            { key: "holidays", label: "Holidays", color: "#10b981" },
            { key: "google", label: "Google", color: "#ea4335" },
            { key: "apple", label: "Apple", color: "#a3aaae" },
            { key: "other", label: "Other", color: "#8b5cf6" },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              className={`filter-chip ${activeCalendars.includes(key) ? "active" : ""}`}
              style={{ "--chip-color": color }}
              onClick={() => toggleCalendar(key)}
            >
              <span className="chip-dot" />
              {label}
            </button>
          ))}
        </div>
        {allTags.length > 0 && (
          <div className="calendar-tag-filters">
            <select
              className="tag-filter-select"
              value={activeTag}
              onChange={(e) => setActiveTag(e.target.value)}
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="calendar-body">
        {view === "month" && (
          <div className="month-grid">
            <div className="weekday-header">
              {WEEKDAYS.map((d) => (
                <div key={d} className="weekday-label">{d}</div>
              ))}
            </div>
            <div className="month-cells">
              {monthDays.map((day, i) => {
                const ds = toDateStr(day);
                const cellData = monthEventRows[ds] || { items: [], totalLanes: 0 };
                const MAX_VISIBLE_LANES = 3;
                const dayEvents = getEventsForDate(day);
                const hiddenCount = dayEvents.length - Math.min(cellData.totalLanes, MAX_VISIBLE_LANES);
                const col = i % 7;
                const tooltipAlign = col <= 1 ? "align-left" : col >= 5 ? "align-right" : "align-center";
                return (
                  <div
                    key={i}
                    className={`month-cell ${isToday(day) ? "today" : ""} ${!isCurrentMonth(day) ? "other-month" : ""} ${expandedDay === ds ? `expanded ${tooltipAlign}` : ""}`}
                  >
                    <span className="day-number">{day.getDate()}</span>
                    <div className="cell-events">
                      {dayEvents.length >= 3 && expandedDay !== ds && (
                        <button
                          className="more-events-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedDay(ds);
                          }}
                        >
                          {hiddenCount > 0 ? `+${hiddenCount}` : "•••"}
                        </button>
                      )}
                      {expandedDay === ds && (
                        <div className="expanded-day-header" style={{ gridRow: 1 }}>
                          <span>{day.getDate()}</span>
                          <button
                            className="popover-close"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDay(null);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                      {(expandedDay === ds ? cellData.items : cellData.items.filter((it) => it.lane < MAX_VISIBLE_LANES))
                        .map((it, idx) => {
                          const isExp = expandedDay === ds;
                          const rowOffset = isExp ? 1 : 0;
                          if (it.spacer) {
                            return <div key={idx} className="event-lane-spacer" style={{ gridRow: it.lane + 1 + rowOffset }} />;
                          }
                          if (it.filler) {
                            const fEv = it.ev;
                            const color = calendarColor(fEv.calendar);
                            return (
                              <div
                                key={idx}
                                className={`event-chip event-bar ${isExp ? "bar-start" : "bar-mid"} ${it.isEnd ? "bar-end" : ""}`}
                                style={{
                                  gridRow: it.lane + 1 + rowOffset,
                                  backgroundColor: color + "22",
                                  borderTopColor: color,
                                  borderBottomColor: color,
                                  borderLeftColor: color,
                                  borderRightColor: color,
                                }}
                                title={formatEventTooltip(fEv)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/schedule/${fEv.id}`);
                                }}
                              >
                                {isExp ? fEv.title : "\u00a0"}
                              </div>
                            );
                          }
                          const ev = it.ev;
                          const isMulti = it.isMultiDay;
                          const endsHere = it.endCol === it.startCol;
                          return (
                            <div
                              key={ev.id + "-" + idx}
                              className={`event-chip ${isMulti ? "event-bar bar-start" : ""} ${isMulti && endsHere ? "bar-end" : ""}`}
                              style={{
                                gridRow: it.lane + 1 + rowOffset,
                                borderLeft: isMulti ? undefined : `3px solid ${calendarColor(ev.calendar)}`,
                                backgroundColor: isMulti ? calendarColor(ev.calendar) + "22" : undefined,
                                borderTopColor: isMulti ? calendarColor(ev.calendar) : undefined,
                                borderBottomColor: isMulti ? calendarColor(ev.calendar) : undefined,
                                borderLeftColor: isMulti ? calendarColor(ev.calendar) : undefined,
                                borderRightColor: isMulti && endsHere ? calendarColor(ev.calendar) : undefined,
                              }}
                              title={formatEventTooltip(ev)}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/schedule/${ev.id}`);
                              }}
                            >
                              {ev.title}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "week" && (
          <div className="week-grid">
            <div className="week-header">
              <div className="time-gutter-header" />
              {weekDays.map((day, i) => (
                <div
                  key={i}
                  className={`week-day-header ${isToday(day) ? "today" : ""}`}
                  onClick={() => { setCurrentDate(day); setView("day"); }}
                >
                  <span className="week-day-name">{WEEKDAYS[i]}</span>
                  <span className="week-day-num">{day.getDate()}</span>
                </div>
              ))}
            </div>
            <div className="week-body" ref={weekBodyRef}>
              <div className="time-gutter">
                {HOURS.map((h) => (
                  <div key={h} className="time-slot-label">
                    {h.toString().padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              {weekDays.map((day, di) => (
                <div key={di} className="week-day-col">
                  {HOURS.map((h) => (
                    <div key={h} className="time-slot" />
                  ))}
                  {getEventsForDate(day).map((ev) => {
                    const startH = parseInt((ev.startTime || "").slice(11, 13)) || 0;
                    const startM = parseInt((ev.startTime || "").slice(14, 16)) || 0;
                    const endH = parseInt((ev.endTime || "").slice(11, 13)) || startH + 1;
                    const endM = parseInt((ev.endTime || "").slice(14, 16)) || 0;
                    const top = (startH + startM / 60) * PX_PER_HOUR;
                    const height = Math.max(((endH + endM / 60) - (startH + startM / 60)) * PX_PER_HOUR, 24);
                    return (
                      <div
                        key={ev.id}
                        className={`week-event priority-${ev.priority || "normal"}`}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          backgroundColor: calendarColor(ev.calendar),
                        }}
                        title={formatEventTooltip(ev)}
                        onClick={() => navigate(`/schedule/${ev.id}`)}
                      >
                        <span className="week-event-title">{ev.title}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "day" && (
          <div className="day-grid">
            <div className="day-body" ref={dayBodyRef}>
              <div className="time-gutter">
                {HOURS.map((h) => (
                  <div key={h} className="time-slot-label">
                    {h.toString().padStart(2, "0")}:00
                  </div>
                ))}
              </div>
              <div className="day-col">
                {HOURS.map((h) => (
                  <div key={h} className="time-slot" />
                ))}
                {getEventsForDate(currentDate).map((ev) => {
                  const startH = parseInt((ev.startTime || "").slice(11, 13)) || 0;
                  const startM = parseInt((ev.startTime || "").slice(14, 16)) || 0;
                  const endH = parseInt((ev.endTime || "").slice(11, 13)) || startH + 1;
                  const endM = parseInt((ev.endTime || "").slice(14, 16)) || 0;
                  const top = (startH + startM / 60) * PX_PER_HOUR;
                  const height = Math.max(((endH + endM / 60) - (startH + startM / 60)) * PX_PER_HOUR, 24);
                  return (
                    <div
                      key={ev.id}
                      className={`day-event priority-${ev.priority || "normal"}`}
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        backgroundColor: calendarColor(ev.calendar),
                      }}
                      title={formatEventTooltip(ev)}
                      onClick={() => navigate(`/schedule/${ev.id}`)}
                    >
                      <strong>{ev.title}</strong>
                      <span>
                        {(ev.startTime || "").slice(11, 16)} – {(ev.endTime || "").slice(11, 16)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendar;
